import React, { useState } from "react";
import { motion } from "framer-motion"; //  Import Framer Motion
import {
  loginUser,
  resetPassword,
  resendActivationEmail,
} from "services/authService";
import { Link, useNavigate } from "react-router-dom";
import logo from "assets/logo/logo.png";
import * as Sentry from "@sentry/react";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await loginUser(email, password);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      navigate("/");
      window.location.reload();
    } catch (error) {
      Sentry.captureException(error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setError("");
    setMessage("");
    try {
      const msg = await resetPassword(email);
      setMessage(msg);
    } catch (error) {
      Sentry.captureException(error);
      setError(error.message);
    }
  };

  const handleResendActivation = async () => {
    setError("");
    setMessage("");
    try {
      const msg = await resendActivationEmail(email);
      setMessage(msg);
    } catch (error) {
      Sentry.captureException(error);
      setError(error.message);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-800 w-full">
      {/* Logo */}
      <motion.img
        src={logo}
        alt="Logo"
        className="w-64 mb-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />

      {/* Animated Login Form */}
      <motion.div
        className="max-w-md p-6 mx-2 bg-white rounded-lg shadow-xl mb-16"
        initial={{ opacity: 0, y: 50 }} // Start slightly below and invisible
        animate={{ opacity: 1, y: 0 }} // Fade in and move up
        transition={{ duration: 0.8, ease: "easeOut" }} // Smooth transition
      >
        <h2 className="text-2xl font-semibold text-center text-gray-700 mb-6">
          Login
        </h2>

        {error && (
          <p className="text-red-500 text-sm text-center mb-4">{error}</p>
        )}
        {message && (
          <p className="text-green-500 text-sm text-center mb-4">{message}</p>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />

          {/* Login Button with Spinner */}
          <button
            type="submit"
            className="w-full p-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition flex items-center justify-center"
            disabled={loading}
          >
            {loading ? (
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              "Login"
            )}
          </button>
        </form>

        {/* Forgot Password Link */}
        <button
          onClick={handlePasswordReset}
          className="mt-3 text-blue-500 hover:underline text-sm block w-full text-center"
        >
          Forgot Password?
        </button>

        {/* Resend Activation Email */}
        <button
          onClick={handleResendActivation}
          className="mt-2 text-blue-500 hover:underline text-sm block w-full text-center"
        >
          Resend Activation Email
        </button>

        <p className="mt-4 text-center text-sm text-gray-600">
          Don't have an account?{" "}
          <Link to="/signup" className="text-blue-500 hover:underline">
            Sign up here
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Login;
